import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Keyboard, X } from 'lucide-react';
import { visibleRoutes } from '@/lib/routes';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';

/**
 * Vim-style "g + X" jump shortcuts and "?" help overlay.
 *
 * <p>Press {@code g} to arm a jump, then a route's letter (e.g. {@code h}
 * → Holdings) within 1.2s. Press {@code ?} any time to see all bindings.
 * Inputs and editable elements are intentionally excluded so typing in a
 * search box never hijacks navigation.
 */
export function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const routes = visibleRoutes(role);
  const [armed, setArmed] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 1200);
    return () => clearTimeout(t);
  }, [armed]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ignore when typing — common bug with these shortcut systems otherwise.
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowHelp((x) => !x);
        return;
      }
      if (e.key === 'Escape') {
        if (showHelp) setShowHelp(false);
        if (armed) setArmed(false);
        return;
      }

      if (e.key === 'g' && !armed) {
        e.preventDefault();
        setArmed(true);
        return;
      }

      if (armed) {
        const route = routes.find((r) => r.shortcut === e.key.toLowerCase());
        if (route) {
          e.preventDefault();
          setArmed(false);
          setShowHelp(false);
          navigate(route.path);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armed, navigate, routes, showHelp]);

  return (
    <>
      {armed && <ArmIndicator />}
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} routes={routes} />}
    </>
  );
}

function ArmIndicator() {
  return (
    <div className="fixed bottom-24 lg:bottom-6 left-6 z-40 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-kam-violet/30 bg-kam-violet-soft text-kam-violet shadow-lg">
      <Keyboard size={12} />
      <span>g + <span className="text-text-faint">letter</span> to jump · esc cancels</span>
    </div>
  );
}

function HelpOverlay({ onClose, routes }: { onClose: () => void; routes: ReturnType<typeof visibleRoutes> }) {
  const groups = (['Today', 'Analyze', 'Operate'] as const)
    .filter((g) => routes.some((r) => r.group === g));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="card max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-2 mb-4">
          <Keyboard size={16} className="text-text-dim" />
          <h2 className="text-sm font-bold flex-1">Keyboard shortcuts</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-surface-2 text-text-dim"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </header>

        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g}>
              <div className="text-[10px] uppercase tracking-wider text-text-faint mb-1.5">{g}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4">
                {routes.filter((r) => r.group === g && r.shortcut).map((r) => (
                  <li key={r.path} className="flex items-center justify-between gap-3 text-xs">
                    <span className="text-text-dim truncate">{r.label}</span>
                    <kbd className="flex-none inline-flex items-center gap-0.5 font-mono text-[10px] text-text-faint">
                      <Key>g</Key><Key>{r.shortcut}</Key>
                    </kbd>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-faint mb-1.5">Help</div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-dim">Toggle this help</span>
              <kbd><Key>?</Key></kbd>
            </div>
          </div>
        </div>

        <p className="mt-4 text-[10px] text-text-faint italic">
          Shortcuts are inert while typing in an input.
        </p>
      </div>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <span className={cn(
      'inline-grid place-items-center min-w-[18px] h-[18px] px-1 rounded border border-[color:var(--border)] bg-surface-2 text-text-dim font-mono text-[10px]',
    )}>{children}</span>
  );
}
